import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './index.css';
import App from './App';
import FinancialPlanningLeadFormPage from './components/FinancialPlanningLeadFormPage';
import FinancialPlanningServicesPage from './components/FinancialPlanningServicesPage';
import Footer from './components/Footer';
import CalculatorsIndexPage from './components/CalculatorsIndexPage';
import HousingDecisionCalculator from './components/HousingDecisionCalculator';
import IncomeChangePaycheckTaxEstimator from './components/IncomeChangePaycheckTaxEstimator';
import PublicLinksPage from './components/PublicLinksPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import RetirementGapEstimator from './components/RetirementGapEstimator';
import RothTraditionalCalculator from './components/RothTraditionalCalculator';
import SocialSecurityEstimator from './components/SocialSecurityEstimator';
import InvestmentPerformanceExplorer from './components/InvestmentPerformanceExplorer';
import CarLeaseBuyCalculator from './components/CarLeaseBuyCalculator';
import ThankYouPage from './components/ThankYouPage';
import PublicSeoMetadata from './components/PublicSeoMetadata';
import ScrollToTop from './components/ScrollToTop';
import CookieConsentBanner from './components/CookieConsentBanner';
import TermsOfUsePage from './components/TermsOfUsePage';
import MeetingBookingPage from './components/MeetingBookingPage';
import MeetingConfirmedPage from './components/MeetingConfirmedPage';
import GoalSettingMeetingPage from './components/GoalSettingMeetingPage';
import AgreementSignedPage from './components/agreements/AgreementSignedPage';
import {
  disableAnalytics,
  initUtmCapture,
  initializeAnalytics,
  isTokenPath,
  preserveUtmParams,
  trackBookedDiscoveryConfirmation,
  trackPageView,
} from './lib/analytics';
import { onConsentChange } from './lib/consent';
import { buildServicesAliasRedirectTarget } from './lib/urlParts';
import reportWebVitals from './reportWebVitals';

// Signing pages (and pdf.js) load only when a signing link is opened.
const AgreementSigningPage = React.lazy(() => import('./components/agreements/AgreementSigningPage'));
const PlannerSigningPage = React.lazy(() => import('./components/agreements/PlannerSigningPage'));
const DocumentSigningPage = React.lazy(() => import('./components/esign/SigningPage'));
const SendApp = React.lazy(() => import('./components/esign/SendApp'));

initializeAnalytics();
initUtmCapture();

function AnalyticsPageView() {
  const location = useLocation();

  React.useEffect(() => {
    // Signing links carry a secret token: no analytics while one is in the URL.
    if (isTokenPath(location.pathname)) return;
    initializeAnalytics();
    preserveUtmParams(location.search);
    initUtmCapture(location.search);
    trackPageView(location.pathname);

    trackBookedDiscoveryConfirmation(location);
  }, [location]);

  React.useEffect(() => onConsentChange(({ analytics }) => {
    if (analytics) {
      initializeAnalytics();
      trackPageView(window.location.pathname);
    } else {
      disableAnalytics();
    }
  }), []);

  return null;
}

function PublicPage({ children }) {
  return (
    <>
      <ScrollToTop />
      {children}
      <Footer />
    </>
  );
}

function setHeadMeta(name, content) {
  let node = document.head.querySelector(`meta[name="${name}"]`);
  const previous = node ? node.getAttribute('content') : null;
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute('name', name);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content);
  return () => {
    if (previous === null) node.remove();
    else node.setAttribute('content', previous);
  };
}

/**
 * Signing pages. The link's token is checked by the backend on every request;
 * here we only keep the URL from leaking through referrers or search indexing.
 */
function TokenPage({ children }) {
  React.useEffect(() => {
    const restoreReferrer = setHeadMeta('referrer', 'no-referrer');
    const restoreRobots = setHeadMeta('robots', 'noindex, nofollow');
    return () => {
      restoreReferrer();
      restoreRobots();
    };
  }, []);

  return (
    <PublicPage>
      <React.Suspense fallback={<p style={{ textAlign: 'center', padding: '3rem 1rem' }}>Loading…</p>}>
        {children}
      </React.Suspense>
    </PublicPage>
  );
}

/** The advisor's document sending area (/send): signed-in advisor only, never indexed. */
function AdvisorToolPage({ children }) {
  React.useEffect(() => setHeadMeta('robots', 'noindex, nofollow'), []);
  return (
    <React.Suspense fallback={<p style={{ textAlign: 'center', padding: '3rem 1rem' }}>Loading…</p>}>
      {children}
    </React.Suspense>
  );
}

/** Old agreement-site paths → their pages here, keeping the query string. */
function RedirectKeepingQuery({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

/** Canonical link-in-bio entry — preserve UTMs when redirecting from /. */
function RootToLinksRedirect() {
  const location = useLocation();
  // Supabase falls back to the site root for email links (password reset, sign-up
  // confirmation) whose return address is not on its allow-list. Those carry the
  // sign-in in the URL, so send them to the portal instead of the links page.
  if (/(?:^|[#&?])(?:access_token=|error_description=|code=|type=recovery)/.test(`${location.search}${location.hash}`)) {
    return <Navigate to={`/login${location.search}${location.hash}`} replace />;
  }
  return <Navigate to={`/links${location.search}${location.hash}`} replace />;
}

/** Friendly Services & Pricing alias — preserve query before fragment. */
function ServicesPricingRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={buildServicesAliasRedirectTarget(location.search, location.hash)}
      replace
    />
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AnalyticsPageView />
      <PublicSeoMetadata />
      <CookieConsentBanner />
      <Routes>
        <Route path="/" element={<RootToLinksRedirect />} />
        <Route path="/links" element={<PublicPage><PublicLinksPage /></PublicPage>} />
        <Route path="/linktree" element={<PublicPage><PublicLinksPage /></PublicPage>} />
        <Route path="/financial-planning-services" element={<PublicPage><FinancialPlanningServicesPage /></PublicPage>} />
        <Route path="/services-and-pricing" element={<ServicesPricingRedirect />} />
        <Route path="/financial-planning-services/request-meeting" element={<PublicPage><FinancialPlanningLeadFormPage /></PublicPage>} />
        <Route path="/privacy-policy" element={<PublicPage><PrivacyPolicyPage /></PublicPage>} />
        <Route path="/privacy-policy-financial-planning" element={<PublicPage><PrivacyPolicyPage /></PublicPage>} />
        <Route path="/terms-of-use" element={<PublicPage><TermsOfUsePage /></PublicPage>} />
        <Route path="/portal/*" element={<App />} />
        <Route path="/login/*" element={<App />} />
        <Route path="/calculators" element={<PublicPage><CalculatorsIndexPage /></PublicPage>} />
        <Route path="/calculators/housing" element={<PublicPage><HousingDecisionCalculator /></PublicPage>} />
        <Route path="/calculators/rent-vs-buy" element={<PublicPage><HousingDecisionCalculator /></PublicPage>} />
        <Route path="/calculators/car-lease-vs-buy" element={<PublicPage><CarLeaseBuyCalculator /></PublicPage>} />
        <Route path="/housing-decision-calculator" element={<PublicPage><HousingDecisionCalculator /></PublicPage>} />
        <Route path="/rent-vs-buy-housing-calculator" element={<PublicPage><HousingDecisionCalculator /></PublicPage>} />
        <Route path="/paycheck-tax-estimator" element={<PublicPage><IncomeChangePaycheckTaxEstimator /></PublicPage>} />
        <Route path="/retirement-gap-estimator" element={<PublicPage><RetirementGapEstimator /></PublicPage>} />
        <Route path="/roth-vs-traditional-calculator" element={<PublicPage><RothTraditionalCalculator /></PublicPage>} />
        <Route path="/social-security-estimator" element={<PublicPage><SocialSecurityEstimator /></PublicPage>} />
        <Route path="/investment-performance-explorer" element={<PublicPage><InvestmentPerformanceExplorer /></PublicPage>} />
        <Route path="/booking-confirmed" element={<PublicPage><ThankYouPage /></PublicPage>} />
        <Route path="/thank-you" element={<PublicPage><ThankYouPage /></PublicPage>} />
        <Route path="/goal-setting-meeting" element={<PublicPage><GoalSettingMeetingPage /></PublicPage>} />
        <Route path="/book" element={<PublicPage><MeetingBookingPage /></PublicPage>} />
        <Route path="/book/confirmed" element={<PublicPage><MeetingConfirmedPage /></PublicPage>} />
        <Route path="/agreement/:token" element={<TokenPage><AgreementSigningPage /></TokenPage>} />
        <Route path="/planner/:token" element={<TokenPage><PlannerSigningPage /></TokenPage>} />
        <Route path="/agreement-signed" element={<PublicPage><AgreementSignedPage /></PublicPage>} />
        <Route path="/sign/:token" element={<TokenPage><DocumentSigningPage /></TokenPage>} />
        <Route path="/send/*" element={<AdvisorToolPage><SendApp /></AdvisorToolPage>} />
        <Route path="/hourly" element={<RedirectKeepingQuery to="/book" />} />
        <Route path="/hourly/success" element={<RedirectKeepingQuery to="/book/confirmed" />} />
        <Route path="/*" element={<App />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();
